import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Product } from './types';

export interface ProductState {
  items: Product[];
  loading: boolean;
  error: string | null;
  currentProduct: Product | null;
  currentProductLoading: boolean;
  currentProductError: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasMore: boolean;
}

export interface FetchProductsRequestPayload {
  categorySlug?: string;
  onSaleOnly?: boolean;
  searchQuery?: string;
  page?: number;
  perPage?: number;
  append?: boolean;
  includeTaxonomyMatches?: boolean;
}

export interface RefreshProductRatingPayload {
  productId: number;
  averageRating: string;
  ratingCount: number;
}

export interface FetchProductBySlugPayload {
  slug: string;
}

const initialState: ProductState = {
  items: [],
  loading: false,
  error: null,
  currentProduct: null,
  currentProductLoading: false,
  currentProductError: null,
  currentPage: 0,
  totalPages: 1,
  totalItems: 0,
  hasMore: false,
};

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearProducts(state) {
      state.items = [];
      state.loading = false;
      state.error = null;
      state.currentPage = 0;
      state.totalPages = 1;
      state.totalItems = 0;
      state.hasMore = false;
    },
    fetchProductsRequest(state, _action: PayloadAction<FetchProductsRequestPayload | undefined>) {
      state.loading = true;
      state.error = null;
      if (!_action.payload?.append) {
        state.items = [];
        state.currentPage = 0;
        state.totalPages = 1;
        state.totalItems = 0;
        state.hasMore = false;
      }
    },
    fetchProductsSuccess(
      state,
      action: PayloadAction<{
        items: Product[];
        page: number;
        totalPages: number;
        totalItems: number;
        append?: boolean;
      }>,
    ) {
      state.loading = false;
      state.items = action.payload.append
        ? [...state.items, ...action.payload.items]
        : action.payload.items;
      state.currentPage = action.payload.page;
      state.totalPages = action.payload.totalPages;
      state.totalItems = action.payload.totalItems;
      state.hasMore = action.payload.page < action.payload.totalPages;
    },
    fetchProductsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    fetchProductBySlugRequest(state, _action: PayloadAction<FetchProductBySlugPayload>) {
      state.currentProduct = null;
      state.currentProductLoading = true;
      state.currentProductError = null;
    },
    fetchProductBySlugSuccess(state, action: PayloadAction<Product>) {
      state.currentProduct = action.payload;
      state.currentProductLoading = false;
      state.currentProductError = null;
    },
    fetchProductBySlugFailure(state, action: PayloadAction<string>) {
      state.currentProduct = null;
      state.currentProductLoading = false;
      state.currentProductError = action.payload;
    },
    refreshProductRating(state, action: PayloadAction<RefreshProductRatingPayload>) {
      const { productId, averageRating, ratingCount } = action.payload;
      const product = state.items.find((item) => item.id === productId);

      if (product) {
        product.average_rating = averageRating;
        product.rating_count = ratingCount;
      }
    },
  },
});

export const {
  clearProducts,
  fetchProductsRequest,
  fetchProductsSuccess,
  fetchProductsFailure,
  fetchProductBySlugRequest,
  fetchProductBySlugSuccess,
  fetchProductBySlugFailure,
  refreshProductRating,
} = productSlice.actions;

export const productReducer = productSlice.reducer;
export default productSlice;
